"use client";
import { useState, FormEvent } from "react";
import Link from "next/link";
import { getBrowserSupabaseClient } from "@wtc/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Mail, Home, LogIn } from "lucide-react";

export default function PasswordResetForm() {
  const supabase = getBrowserSupabaseClient();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Reset all states
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${new URL(location.href).origin}/auth/callback`,
      });

      if (error) {
        setError(error.message);
        return;
      }

      // Reset form
      setEmail("");
      setSuccess(
        "Password reset email sent! Check your email and click the link to reset your password."
      );
    } catch (err) {
      console.error("Password reset error:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="space-y-6">
        {/* Top navigation — give the user a clear way back to home or sign-in */}
        <nav className="flex items-center justify-between" aria-label="Page navigation">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            aria-label="Go to home"
          >
            <Home className="h-4 w-4" />
            <span>Home</span>
          </Link>
          <Link
            href="/auth/employee"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            aria-label="Go to sign in"
          >
            <LogIn className="h-4 w-4" />
            <span>Sign in</span>
          </Link>
        </nav>

        <div className="text-center">
          <h2 className="text-2xl font-bold">Reset Password</h2>
          <p className="text-muted-foreground">Enter your email to receive a password reset link</p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Mail className="mr-2 h-4 w-4" />
            Send Reset Link
          </Button>
        </form>

        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Remember your password?{" "}
            <Link href="/auth/magic-link" className="text-primary hover:underline">
              Sign in with magic link
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
